"use client";

import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors duration-fast ease-standard focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary: "bg-accent text-accent-fg hover:bg-accent/90 active:bg-accent/80",
        secondary: "bg-muted text-fg hover:bg-muted/80 active:bg-muted/70",
        destructive: "bg-danger text-danger-fg hover:bg-danger/90 active:bg-danger/80",
        outline: "border border-border-strong bg-bg text-fg hover:bg-muted",
        ghost: "text-fg hover:bg-muted active:bg-muted/80",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-[var(--aio-button-height,2.75rem)] px-4 text-sm",
        lg: "h-12 px-6 text-base",
        icon: "h-[var(--aio-button-height,2.75rem)] w-[var(--aio-button-height,2.75rem)]",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, loading = false, disabled, children, ...props },
  ref,
) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
      <Slottable>{children}</Slottable>
    </Comp>
  );
});

export { buttonVariants };
